'use client'

import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef } from 'react'

export function usePerformanceTracking() {
  const posthog = usePostHog()
  const pageLoadTracked = useRef(false)

  useEffect(() => {
    if (!posthog || pageLoadTracked.current) return

    // Track page load performance
    const trackPageLoad = () => {
      if (typeof window === 'undefined' || !window.performance) return

      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming

      if (navigation) {
        posthog.capture('page_load_performance', {
          // Core Web Vitals and timing metrics
          load_time_ms: navigation.loadEventEnd - navigation.fetchStart,
          dom_ready_ms: navigation.domContentLoadedEventEnd - navigation.fetchStart,
          dns_lookup_ms: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcp_connect_ms: navigation.connectEnd - navigation.connectStart,
          request_response_ms: navigation.responseEnd - navigation.requestStart,
          dom_processing_ms: navigation.domComplete - navigation.domContentLoadedEventStart,

          // Page info
          url: window.location.href,
          referrer: document.referrer,

          // Device info
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          device_memory: (navigator as unknown as Record<string, unknown>).deviceMemory || 'unknown',
          connection_type: ((navigator as unknown as Record<string, unknown>).connection as Record<string, unknown>)?.effectiveType || 'unknown',

          timestamp: new Date().toISOString()
        })

        pageLoadTracked.current = true
      }
    }

    // Wait for page to be fully loaded
    if (document.readyState === 'complete') {
      trackPageLoad()
    } else {
      window.addEventListener('load', trackPageLoad)
      return () => window.removeEventListener('load', trackPageLoad)
    }
  }, [posthog])

  // Helper function to track custom performance metrics
  const trackCustomMetric = (metricName: string, duration: number, context?: Record<string, unknown>) => {
    if (!posthog) return

    posthog.capture(metricName, {
      duration_ms: duration,
      context: context || {},
      url: window.location.href,
      timestamp: new Date().toISOString()
    })
  }

  // Helper function to measure and track operation duration
  const measureOperation = async <T>(
    operationName: string,
    operation: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<T> => {
    const startTime = performance.now()

    try {
      const result = await operation()
      const duration = performance.now() - startTime

      trackCustomMetric(operationName, duration, {
        ...context,
        success: true
      })

      return result
    } catch (error) {
      const duration = performance.now() - startTime

      trackCustomMetric(operationName, duration, {
        ...context,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      throw error
    }
  }

  return { trackCustomMetric, measureOperation }
}